import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocQRInline,
  DocInlineDate,
  DocInlineChips,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DraftBanner,
  DocNotes,
  createDocNote,
  type DocNote,
  SavedDocsButton,
  NewDocButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
  type DocRowsSnapshot,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  ContactPickerButton,
  type AresPickResult,
  type CompanyPickResult,
  type ContactPerson,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Předávací protokol — staveniště.
 * "Živý dokument": papír je editovatelný formulář, akce (📍 Mapa, 🔍 ARES,
 * 📇 Adresář, 📱 QR…) sedí přímo u řádků a jsou skryté při tisku/PDF.
 */

type UcastnikPreset = 'tdi' | 'tds' | 'bozp' | 'autorsky' | 'other';

interface Ucastnik {
  id: string;
  preset: UcastnikPreset;
  strana: string;
  firma: string;
  jmeno: string;
  funkce: string;
  tel: string;
  email: string;
}

const PRESET_LABELS: Record<UcastnikPreset, { strana: string; funkce: string }> = {
  tdi: { strana: 'Technický dozor investora (TDI)', funkce: 'TDI / TDS' },
  tds: { strana: 'Technický dozor stavebníka (TDS)', funkce: 'TDS' },
  bozp: { strana: 'Koordinátor BOZP', funkce: 'Koordinátor BOZP' },
  autorsky: { strana: 'Autorský dozor', funkce: 'Autorský dozor' },
  other: { strana: 'Další účastník', funkce: '' },
};

type ProtocolData = {
  nazev: string;
  adresa: string;
  cPovoleni: string;
  cZakazky: string;
  investor: {
    name: string;
    address: string;
    ico: string;
    zastoupeny: string;
    phone: string;
    email: string;
  };
  zhotovitel: {
    name: string;
    address: string;
    ico: string;
    ckait: string;
    stavbyvedouci: string;
    phone: string;
    email: string;
  };
  datumPredani: string;
  zahajeni: string;
  dokonceni: string;
  stavStaveniste: string;
  poznamky: string;
  ucastnici: Ucastnik[];
  /** Volitelné textové rámečky sekce „Ostatní". */
  ostatni: DocNote[];
  signatures: {
    investor: string;
    zhotovitel: string;
    ucastnici: Record<string, string>;
  };
};

const initialData: ProtocolData = {
  nazev: '',
  adresa: '',
  cPovoleni: '',
  cZakazky: '',
  investor: {
    name: '',
    address: '',
    ico: '',
    zastoupeny: '',
    phone: '',
    email: '',
  },
  zhotovitel: {
    name: '',
    address: '',
    ico: '',
    ckait: '',
    stavbyvedouci: '',
    phone: '',
    email: '',
  },
  datumPredani: '',
  zahajeni: '',
  dokonceni: '',
  stavStaveniste: '',
  poznamky: '',
  ucastnici: [],
  ostatni: [],
  signatures: { investor: '', zhotovitel: '', ucastnici: {} },
};

const docChipsInitial = [
  { id: 'pd-provadeni', label: 'PD pro provádění', checked: true },
  { id: 'povoleni', label: 'Stavební povolení', checked: true },
  { id: 'geodet', label: 'Geodetické podklady', checked: true },
  { id: 'bozp', label: 'Projekt BOZP', checked: false },
  { id: 'site', label: 'PD inž. sítí', checked: false },
];
const fixedChipIds = docChipsInitial.map((c) => c.id);

function newUcastnikId(): string {
  return `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function createUcastnik(preset: UcastnikPreset): Ucastnik {
  const p = PRESET_LABELS[preset];
  return {
    id: newUcastnikId(),
    preset,
    strana: p.strana,
    funkce: p.funkce,
    firma: '',
    jmeno: '',
    tel: '',
    email: '',
  };
}

type ProtocolDraft = ProtocolData & { docChips: typeof docChipsInitial; radky?: DocRowsSnapshot };

export function ProtocolPage() {
  const [data, setData] = useState<ProtocolData>(initialData);
  const [docChips, setDocChips] = useState(docChipsInitial);
  const [emailOpen, setEmailOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();

  // Skryté řádky (✕ + „obnovit" v hlavičce sekce) a vlastní řádky („+ Řádek").
  // Ukládá se s dokumentem i do konceptu jako `radky`.
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  // Podtitulek pod nadpisem dokumentu (č. zakázky · datum předání). Skládá se
  // jen z toho, co je opravdu vyplněné — když není nic, řádek se nevykreslí
  // (dřív zbylo holé „č.  ·").
  const zakazkaValue = isHidden('s01.zakazky') ? '' : data.cZakazky.trim();
  const docMeta = [zakazkaValue && `č. ${zakazkaValue}`, data.datumPredani.trim()]
    .filter(Boolean)
    .join(' · ');

  const setSignature = (who: 'investor' | 'zhotovitel', dataUrl: string) =>
    setData((d) => ({ ...d, signatures: { ...d.signatures, [who]: dataUrl } }));
  const setUcastnikSignature = (id: string, dataUrl: string) =>
    setData((d) => ({
      ...d,
      signatures: {
        ...d.signatures,
        ucastnici: { ...d.signatures.ucastnici, [id]: dataUrl },
      },
    }));

  const { editId } = useDocEditor((raw) => {
    const { docChips: savedChips, radky, ...rest } = raw;
    setData((prev) => ({ ...prev, ...(rest as Partial<ProtocolData>) }));
    if (savedChips) setDocChips(savedChips as typeof docChipsInitial);
    rows.apply(radky);
  });

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového formuláře).
  const draft = useDocDraft<ProtocolDraft>('predavaci', editId == null);
  useEffect(() => {
    draft.persist({ ...data, docChips, radky: rows.snapshot() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, docChips, rows]);

  function restoreDraft() {
    if (!draft.pending) return;
    const { docChips: dc, radky, ...rest } = draft.pending.data;
    setData((prev) => ({ ...prev, ...(rest as Partial<ProtocolData>) }));
    if (dc) setDocChips(dc);
    rows.apply(radky);
    draft.accept();
  }

  async function handleSave() {
    try {
      await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'predavaci',
        title: `Předávací protokol ${zakazkaValue || data.nazev}`,
        stavba: activeStavba?.nazev ?? '',
        data_json: { ...data, docChips, radky: rows.snapshot() },
      });
      draft.clear();
      toast.success('Dokument uložen do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  const filled = useMemo(() => {
    const allValues = [
      data.nazev, data.adresa,
      ...(isHidden('s01.povoleni') ? [] : [data.cPovoleni]),
      ...(isHidden('s01.zakazky') ? [] : [data.cZakazky]),
      data.investor.name, data.investor.address, data.investor.ico, data.investor.zastoupeny, data.investor.phone, data.investor.email,
      data.zhotovitel.name, data.zhotovitel.address, data.zhotovitel.ico, data.zhotovitel.ckait, data.zhotovitel.stavbyvedouci, data.zhotovitel.phone, data.zhotovitel.email,
      data.datumPredani, data.zahajeni, data.dokonceni,
    ];
    const total = allValues.length;
    const filledCount = allValues.filter((v) => v && v.trim().length > 0).length;
    return Math.round((filledCount / total) * 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, rows]);

  const toggleChip = (id: string) =>
    setDocChips((prev) => prev.map((c) => (c.id === id ? { ...c, checked: !c.checked } : c)));
  const addChip = (label: string) =>
    setDocChips((prev) => [
      ...prev,
      { id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, label, checked: true },
    ]);
  const removeChip = (id: string) => setDocChips((prev) => prev.filter((c) => c.id !== id));

  const set = <K extends keyof ProtocolData>(key: K, value: ProtocolData[K]) =>
    setData((d) => ({ ...d, [key]: value }));
  const setInvestor = (patch: Partial<ProtocolData['investor']>) =>
    setData((d) => ({ ...d, investor: { ...d.investor, ...patch } }));
  const setZhotovitel = (patch: Partial<ProtocolData['zhotovitel']>) =>
    setData((d) => ({ ...d, zhotovitel: { ...d.zhotovitel, ...patch } }));

  const addUcastnik = (preset: UcastnikPreset) =>
    setData((d) => ({ ...d, ucastnici: [...d.ucastnici, createUcastnik(preset)] }));
  const updateUcastnik = (id: string, patch: Partial<Ucastnik>) =>
    setData((d) => ({
      ...d,
      ucastnici: d.ucastnici.map((u) => (u.id === id ? { ...u, ...patch } : u)),
    }));
  const removeUcastnik = (id: string) =>
    setData((d) => ({ ...d, ucastnici: d.ucastnici.filter((u) => u.id !== id) }));

  // Volitelná sekce „Ostatní" — libovolný počet textových rámečků.
  const addOstatni = () => setData((d) => ({ ...d, ostatni: [...d.ostatni, createDocNote()] }));
  const updateOstatni = (id: string, patch: Partial<DocNote>) =>
    setData((d) => ({
      ...d,
      ostatni: d.ostatni.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
  const removeOstatni = (id: string) =>
    setData((d) => ({ ...d, ostatni: d.ostatni.filter((n) => n.id !== id) }));

  const addUcastnikActions = (
    <>
      <DocActionButton onClick={() => addUcastnik('tdi')}>+ TDI</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('tds')}>+ TDS</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('bozp')}>+ BOZP</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('autorsky')}>+ Autorský</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('other')}>+ Vlastní</DocActionButton>
    </>
  );

  return (
    <>
      <DocLayout
        breadcrumb={['Předání', 'Staveniště']}
        title="Protokol o předání staveniště"
        statusText="Rozpracovaný koncept"
        status="draft"
        formProgress={{ percent: filled, label: 'Vyplněno' }}
        actions={
          <>
            <NewDocButton draftKey="predavaci" path="/app/predavaci" />
            <SavedDocsButton
              docType="predavaci"
              title="Uložené předávací protokoly"
              currentId={editId}
            />
            <Button
              variant="secondary"
              leading="⎙"
              onClick={() =>
                printToPdf(
                  `Predavaci_protokol_${zakazkaValue || data.nazev || 'staveniste'}.pdf`,
                )
              }
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: 'SN',
              name: data.zhotovitel.name,
              address: `${data.zhotovitel.address || 'Adresa'} · IČO ${data.zhotovitel.ico}`,
            }}
            tag="Předávací protokol"
            title="Protokol o předání a převzetí staveniště"
            meta={docMeta}
            footer={{ left: data.zhotovitel.name, center: 'str. 1 / 1', right: zakazkaValue }}
          >
            <DocBlock
              idx="01"
              title="Identifikace stavby"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton
                    title="Načíst stavbu z adresáře"
                    onPick={(s: StavbaPickResult) =>
                      setData((d) => ({
                        ...d,
                        nazev: s.nazev,
                        adresa: s.adresa,
                        cPovoleni: s.stavebni_povoleni,
                        cZakazky: s.zakazka_cislo,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu do adresáře"
                    build={() => ({
                      nazev: data.nazev,
                      adresa: data.adresa,
                      stavebni_povoleni: data.cPovoleni,
                      zakazka_cislo: data.cZakazky,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s01.nazev') && (
                <DocRow
                  k="Název stavby"
                  value={data.nazev}
                  onChange={(v) => set('nazev', v)}
                  placeholder="např. Novostavba RD…"
                  onRemove={() => hide('s01.nazev')}
                />
              )}
              {!isHidden('s01.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={data.adresa}
                  onChange={(v) => set('adresa', v)}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.adresa}
                      onApply={(v) => set('adresa', v)}
                      title="Vybrat adresu staveniště"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s01.adresa')}
                />
              )}
              {!isHidden('s01.povoleni') && (
                <DocRow
                  k="Č. stav. povolení"
                  mono
                  value={data.cPovoleni}
                  onChange={(v) => set('cPovoleni', v)}
                  placeholder="např. MÚ-2026/0341 (nepovinné)"
                  actions={
                    <DocActionButton
                      title="Stavba žádné povolení nemá — řádek se v dokumentu nezobrazí"
                      onClick={() => hide('s01.povoleni')}
                    >
                      Nevyplňovat
                    </DocActionButton>
                  }
                  onRemove={() => hide('s01.povoleni')}
                  removeTitle="Nevyplňovat — řádek se nezobrazí"
                />
              )}
              {!isHidden('s01.zakazky') && (
                <DocRow
                  k="Číslo zakázky"
                  mono
                  value={data.cZakazky}
                  onChange={(v) => set('cZakazky', v)}
                  placeholder="např. Z-2026/0412 (nepovinné)"
                  actions={
                    <DocActionButton
                      title="Zakázka nemá číslo — řádek se v dokumentu nezobrazí"
                      onClick={() => hide('s01.zakazky')}
                    >
                      Nevyplňovat
                    </DocActionButton>
                  }
                  onRemove={() => hide('s01.zakazky')}
                  removeTitle="Nevyplňovat — řádek se nezobrazí"
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Předávající (Investor)"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst investora z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setInvestor({
                        name: c.nazev,
                        ico: c.ico,
                        address: c.adresa,
                        zastoupeny: c.kontakt_jmeno,
                        phone: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit investora do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: data.investor.name,
                      ico: data.investor.ico,
                      adresa: data.investor.address,
                      kontakt_jmeno: data.investor.zastoupeny,
                      kontakt_tel: data.investor.phone,
                      kontakt_email: data.investor.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.firma') && (
                <DocRow
                  k="Firma / Jméno"
                  v={
                    <DocInlineAresName
                      value={data.investor.name}
                      onChange={(v) => setInvestor({ name: v })}
                      placeholder="jméno investora / firma"
                      onPick={(d: AresPickResult) =>
                        setInvestor({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.firma')}
                />
              )}
              {!isHidden('s02.adresa') && (
                <DocRow
                  k="Adresa"
                  value={data.investor.address}
                  onChange={(v) => setInvestor({ address: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.investor.address}
                      onApply={(v) => setInvestor({ address: v })}
                      title="Adresa investora"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.adresa')}
                />
              )}
              {!isHidden('s02.ico') && (
                <DocRow
                  k="IČO"
                  mono
                  value={data.investor.ico}
                  onChange={(v) => setInvestor({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={data.investor.ico}
                      onResult={(d) =>
                        setInvestor({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.ico')}
                />
              )}
              {!isHidden('s02.zastoupeny') && (
                <DocRow
                  k="Zastoupený"
                  value={data.investor.zastoupeny}
                  onChange={(v) => setInvestor({ zastoupeny: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={data.investor.name}
                      companyDraft={{ role: 'objednatel', ico: data.investor.ico, adresa: data.investor.address }}
                      draftNew={{
                        jmeno: data.investor.zastoupeny,
                        tel: data.investor.phone,
                        email: data.investor.email,
                      }}
                      title="Zástupce investora — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setInvestor({
                          zastoupeny: p.jmeno,
                          ...(p.tel ? { phone: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s02.zastoupeny')}
                />
              )}
              {!isHidden('s02.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={data.investor.phone}
                  onChange={(v) => setInvestor({ phone: v })}
                  inputType="tel"
                  placeholder="+420 …"
                  onRemove={() => hide('s02.tel')}
                />
              )}
              {!isHidden('s02.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={data.investor.email}
                  onChange={(v) => setInvestor({ email: v })}
                  inputType="email"
                  placeholder="email@…"
                  actions={
                    <DocContactQR
                      name={data.investor.zastoupeny || data.investor.name}
                      role="Investor"
                      org={data.investor.name}
                      phone={data.investor.phone}
                      email={data.investor.email}
                      title="QR vizitka — investor"
                    />
                  }
                  onRemove={() => hide('s02.email')}
                />
              )}
              {!isHidden('s02.qr') && (
                <DocRow
                  k="QR vizitka"
                  v={
                    <DocQRInline
                      name={data.investor.zastoupeny || data.investor.name}
                      role="Investor"
                      org={data.investor.name}
                      phone={data.investor.phone}
                      email={data.investor.email}
                      caption="Investor"
                    />
                  }
                  onRemove={() => hide('s02.qr')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Přebírající (Zhotovitel)"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setZhotovitel({
                        name: c.nazev,
                        ico: c.ico,
                        address: c.adresa,
                        ckait: c.ckait,
                        stavbyvedouci: c.stavbyvedouci || c.kontakt_jmeno,
                        phone: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: data.zhotovitel.name,
                      ico: data.zhotovitel.ico,
                      adresa: data.zhotovitel.address,
                      kontakt_jmeno: data.zhotovitel.stavbyvedouci,
                      kontakt_tel: data.zhotovitel.phone,
                      kontakt_email: data.zhotovitel.email,
                      stavbyvedouci: data.zhotovitel.stavbyvedouci,
                      ckait: data.zhotovitel.ckait,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.firma') && (
                <DocRow
                  k="Firma"
                  v={
                    <DocInlineAresName
                      value={data.zhotovitel.name}
                      onChange={(v) => setZhotovitel({ name: v })}
                      placeholder="např. Stavby Novák s.r.o."
                      onPick={(d: AresPickResult) =>
                        setZhotovitel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.firma')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={data.zhotovitel.address}
                  onChange={(v) => setZhotovitel({ address: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.zhotovitel.address}
                      onApply={(v) => setZhotovitel({ address: v })}
                      title="Sídlo zhotovitele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČO"
                  mono
                  value={data.zhotovitel.ico}
                  onChange={(v) => setZhotovitel({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={data.zhotovitel.ico}
                      onResult={(d) =>
                        setZhotovitel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.stavbyvedouci') && (
                <DocRow
                  k="Stavbyvedoucí"
                  value={data.zhotovitel.stavbyvedouci}
                  onChange={(v) => setZhotovitel({ stavbyvedouci: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={data.zhotovitel.name}
                      companyDraft={{ role: 'zhotovitel', ico: data.zhotovitel.ico, adresa: data.zhotovitel.address }}
                      draftNew={{
                        jmeno: data.zhotovitel.stavbyvedouci,
                        funkce: 'stavbyvedoucí',
                        tel: data.zhotovitel.phone,
                        email: data.zhotovitel.email,
                      }}
                      title="Stavbyvedoucí — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setZhotovitel({
                          stavbyvedouci: p.jmeno,
                          ...(p.tel ? { phone: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s03.stavbyvedouci')}
                />
              )}
              {!isHidden('s03.ckait') && (
                <DocRow
                  k="Č. autorizace ČKAIT"
                  mono
                  value={data.zhotovitel.ckait}
                  onChange={(v) => setZhotovitel({ ckait: v })}
                  placeholder="např. 1002847"
                  onRemove={() => hide('s03.ckait')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={data.zhotovitel.phone}
                  onChange={(v) => setZhotovitel({ phone: v })}
                  inputType="tel"
                  placeholder="+420 …"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={data.zhotovitel.email}
                  onChange={(v) => setZhotovitel({ email: v })}
                  inputType="email"
                  placeholder="email@…"
                  actions={
                    <DocContactQR
                      name={data.zhotovitel.stavbyvedouci || data.zhotovitel.name}
                      role="Stavbyvedoucí"
                      org={data.zhotovitel.name}
                      phone={data.zhotovitel.phone}
                      email={data.zhotovitel.email}
                      title="QR vizitka — stavbyvedoucí"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              {!isHidden('s03.qr') && (
                <DocRow
                  k="QR vizitka"
                  v={
                    <DocQRInline
                      name={data.zhotovitel.stavbyvedouci || data.zhotovitel.name}
                      role="Stavbyvedoucí"
                      org={data.zhotovitel.name}
                      phone={data.zhotovitel.phone}
                      email={data.zhotovitel.email}
                      caption="Stavbyvedoucí"
                    />
                  }
                  onRemove={() => hide('s03.qr')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock idx="04" title="Další účastníci" actions={addUcastnikActions}>
              {data.ucastnici.length === 0 ? (
                <DocRow
                  k="—"
                  v={
                    <span style={{ color: 'var(--sd-doc-dim)', fontStyle: 'italic' }}>
                      Žádní další účastníci. Přidej je tlačítky vpravo.
                    </span>
                  }
                />
              ) : (
                data.ucastnici.map((u, i) => (
                  <UcastnikRows
                    key={u.id}
                    u={u}
                    index={i}
                    onUpdate={(patch) => updateUcastnik(u.id, patch)}
                    onRemove={() => removeUcastnik(u.id)}
                  />
                ))
              )}
            </DocBlock>

            <DocBlock
              idx="05"
              title="Předmět předání"
              hiddenCount={hiddenCount('s05')}
              onRestoreHidden={() => restoreSection('s05')}
              actions={<DocAddRowButton rows={rows} section="s05" />}
            >
              {!isHidden('s05.datumPredani') && (
                <DocRow
                  k="Datum předání"
                  mono
                  value={data.datumPredani}
                  onChange={(v) => set('datumPredani', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.datumPredani}
                      onChange={(v) => set('datumPredani', v)}
                    />
                  }
                  onRemove={() => hide('s05.datumPredani')}
                />
              )}
              {!isHidden('s05.zahajeni') && (
                <DocRow
                  k="Zahájení prací"
                  mono
                  value={data.zahajeni}
                  onChange={(v) => set('zahajeni', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.zahajeni}
                      onChange={(v) => set('zahajeni', v)}
                    />
                  }
                  onRemove={() => hide('s05.zahajeni')}
                />
              )}
              {!isHidden('s05.dokonceni') && (
                <DocRow
                  k="Dokončení prací"
                  mono
                  value={data.dokonceni}
                  onChange={(v) => set('dokonceni', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.dokonceni}
                      onChange={(v) => set('dokonceni', v)}
                    />
                  }
                  onRemove={() => hide('s05.dokonceni')}
                />
              )}
              {!isHidden('s05.doklady') && (
                <DocRow
                  k="Předané doklady"
                  v={
                    <DocInlineChips
                      items={docChips}
                      onToggle={toggleChip}
                      onAdd={addChip}
                      onRemove={removeChip}
                      fixedIds={fixedChipIds}
                    />
                  }
                  onRemove={() => hide('s05.doklady')}
                />
              )}
              {!isHidden('s05.stav') && (
                <DocRow
                  k="Stav staveniště"
                  multiline
                  value={data.stavStaveniste}
                  onChange={(v) => set('stavStaveniste', v)}
                  placeholder="Úrovně terénu, přístup, napojení sítí, oplocení…"
                  onRemove={() => hide('s05.stav')}
                />
              )}
              {!isHidden('s05.poznamky') && (
                <DocRow
                  k="Poznámky / závady"
                  multiline
                  value={data.poznamky}
                  onChange={(v) => set('poznamky', v)}
                  placeholder="Vady, výhrady, nesouhlasy"
                  onRemove={() => hide('s05.poznamky')}
                />
              )}
              <DocCustomRows rows={rows} section="s05" />
            </DocBlock>

            {data.ostatni.length > 0 ? (
              <DocBlock
                idx="06"
                title="Ostatní"
                actions={
                  <DocActionButton onClick={addOstatni}>+ Přidat rámeček</DocActionButton>
                }
              >
                <DocNotes items={data.ostatni} onChange={updateOstatni} onRemove={removeOstatni} />
              </DocBlock>
            ) : (
              <div className="docNoPrint" style={{ marginTop: 14 }}>
                <DocActionButton
                  onClick={addOstatni}
                  title="Volitelná sekce — poznámky, připomínky, vyjádření TDS…"
                >
                  + Přidat sekci „Ostatní"
                </DocActionButton>
              </div>
            )}

            <DocSignatures
              items={[
                {
                  label: 'Investor (předávající)',
                  name: data.investor.zastoupeny || data.investor.name,
                  role: 'předávající',
                  image: data.signatures.investor,
                  onSign: (v) => setSignature('investor', v),
                },
                {
                  label: 'Zhotovitel (přebírající)',
                  name: data.zhotovitel.stavbyvedouci,
                  role: 'stavbyvedoucí',
                  image: data.signatures.zhotovitel,
                  onSign: (v) => setSignature('zhotovitel', v),
                },
                ...data.ucastnici.map((u) => ({
                  label: u.strana || 'Další účastník',
                  name: u.jmeno || '—',
                  role: u.funkce || '',
                  image: data.signatures.ucastnici[u.id] || '',
                  onSign: (v: string) => setUcastnikSignature(u.id, v),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="predavaci"
        docTitle={`Předávací protokol — ${data.nazev || 'staveniště'}`}
        defaultTo={data.investor.email}
        defaultCc={data.zhotovitel.email}
        defaultSubject={`Předávací protokol staveniště — ${data.nazev}${zakazkaValue ? ' (č. ' + zakazkaValue + ')' : ''}`}
        meta={`Stavba: ${data.nazev || '—'}${zakazkaValue ? ' · Zakázka: ' + zakazkaValue : ''}${activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''}`}
      />
    </>
  );
}

/** Účastník vykreslený přímo jako sekce řádků v dokumentu. */
function UcastnikRows({
  u,
  index,
  onUpdate,
  onRemove,
}: {
  u: Ucastnik;
  index: number;
  onUpdate: (patch: Partial<Ucastnik>) => void;
  onRemove: () => void;
}) {
  const preferRole =
    u.preset === 'tdi' || u.preset === 'tds'
      ? 'tds'
      : u.preset === 'bozp'
        ? 'bozp_koordinator'
        : u.preset === 'autorsky'
          ? 'projektant'
          : undefined;

  const saveRole =
    u.preset === 'bozp'
      ? 'bozp_koordinator'
      : u.preset === 'tdi' || u.preset === 'tds'
        ? 'tds'
        : u.preset === 'autorsky'
          ? 'projektant'
          : 'subdodavatel';

  return (
    <>
      <DocRow
        k={`Účastník ${index + 1}`}
        value={u.strana}
        onChange={(v) => onUpdate({ strana: v })}
        placeholder="Strana / role"
        actions={
          <>
            <CompanyPickerButton
              preferRole={preferRole}
              title={`Načíst ${u.strana} z adresáře`}
              onPick={(c) =>
                onUpdate({
                  firma: c.nazev,
                  jmeno: c.kontakt_jmeno || c.stavbyvedouci || u.jmeno,
                  tel: c.kontakt_tel,
                  email: c.kontakt_email,
                })
              }
            />
            <SaveCompanyButton
              title={`Uložit ${u.strana} do adresáře`}
              build={() => ({
                role: saveRole,
                nazev: u.firma || u.strana,
                kontakt_jmeno: u.jmeno,
                kontakt_tel: u.tel,
                kontakt_email: u.email,
                bozp_koordinator: u.preset === 'bozp' ? u.jmeno : '',
                bozp_tel: u.preset === 'bozp' ? u.tel : '',
                poznamka: u.funkce,
              })}
            />
            <DocActionButton onClick={onRemove} title="Odebrat účastníka">
              ✕
            </DocActionButton>
          </>
        }
      />
      <DocRow
        k="Firma"
        value={u.firma}
        onChange={(v) => onUpdate({ firma: v })}
        placeholder="název firmy"
      />
      <DocRow
        k="Jméno a příjmení"
        value={u.jmeno}
        onChange={(v) => onUpdate({ jmeno: v })}
        placeholder="jméno – vyplňte nebo vyberte z adresáře"
      />
      <DocRow
        k="Funkce"
        value={u.funkce}
        onChange={(v) => onUpdate({ funkce: v })}
        placeholder="funkce"
      />
      <DocRow
        k="Telefon"
        mono
        value={u.tel}
        onChange={(v) => onUpdate({ tel: v })}
        inputType="tel"
        placeholder="+420 …"
      />
      <DocRow
        k="E-mail"
        mono
        value={u.email}
        onChange={(v) => onUpdate({ email: v })}
        inputType="email"
        placeholder="email@…"
        actions={
          <DocContactQR
            name={u.jmeno}
            role={u.funkce || u.strana}
            org={u.firma}
            phone={u.tel}
            email={u.email}
            compact
            label="QR"
            title={`QR vizitka — ${u.strana}`}
          />
        }
      />
    </>
  );
}
